/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Combobox } from 'app/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { PiggyBank, Plus, Split, Trash2, X } from 'lucide-react';
import { useCryptoGestFxAt } from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { useDisplayMoney } from 'app/components/pages/crypto-gest/use-display-money';
import { cn } from 'app/lib/utils';
import {
  PiCgLink,
  useCreatePoolInvestFromCryptoGest,
  usePoolInvestProject,
  usePoolInvestProjects,
  useRemovePoolInvestCryptoGestLinks
} from 'app/hooks/requests/pool-invest/use-pool-invest';

/**
 * The transaction fields the pool actions need — a minimal shape so both the
 * ledger (`CgTransaction`) and the per-asset detail (`CgAssetWalletTx` + symbol)
 * can drive these controls.
 */

export type PoolAllocatableTx = {
  id: string;
  symbol: string;
  amount: string;
  unitPriceFiat: string | null;
  currency: 'USD' | 'EUR';
  timestamp: string;
  direction: 'in' | 'out';
};

/**
 * Per-unit price of the transaction in the display currency (EUR): its own
 * historical price converted from the trade currency. Lets amount and quantity
 * stay linked (amount = qty × unit) instead of being free-form.
 */

function useTxPricing(tx: PoolAllocatableTx): { unitEur: number; unitUsd: number } {
  const { data: fx } = useCryptoGestFxAt(tx.timestamp);
  const { usdPerEur: current } = useDisplayMoney();
  // USD per EUR at the transaction's date (so a fixed-EUR contribution doesn't
  // drift with FX); falls back to the current rate until it loads.
  const usdPerEur = fx?.usdPerEur ?? current;
  const unit = parseFloat(tx.unitPriceFiat ?? '0') || 0;

  if (tx.currency === 'EUR') {
    return { unitEur: unit, unitUsd: usdPerEur > 0 ? unit * usdPerEur : 0 };
  }

  return { unitEur: usdPerEur > 0 ? unit / usdPerEur : 0, unitUsd: unit };
}

function useUnitPriceDisplay(tx: PoolAllocatableTx): number {
  return useTxPricing(tx).unitEur;
}

/**
 * Round a display-currency amount / a crypto quantity to sane precision.
 */

function money2(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : '';
}

function qty8(value: number): string {
  return Number.isFinite(value) ? String(parseFloat(value.toFixed(8))) : '';
}

/**
 * Format a value in a given currency (e.g. `150,00 €` / `$168.00`).
 */

function money(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Format a crypto quantity compactly (up to 8 dp, no trailing zeros).
 */

function fmtQty(value: string | null): string {
  if (!value) {
    return '0';
  }

  return String(parseFloat(parseFloat(value).toFixed(8)));
}

/**
 * Category picker for a chosen project (loads that project's buckets).
 */

function useCategoryOptions(projectId: string) {
  const { data: project } = usePoolInvestProject(projectId || undefined);

  return useMemo(() => (project?.buckets ?? []).map(bucket => ({ value: bucket.id, label: bucket.name })), [project]);
}

/**
 * Assign the whole transaction to a single pool.
 */

function AssignDialog({ tx, onClose }: { tx: PoolAllocatableTx; onClose: () => void }) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: projects } = usePoolInvestProjects();
  const create = useCreatePoolInvestFromCryptoGest();

  const [projectId, setProjectId] = useState('');
  const [bucketId, setBucketId] = useState('');
  const [quantity, setQuantity] = useState(tx.amount);
  const [amount, setAmount] = useState('');
  const [touched, setTouched] = useState<'qty' | 'amount' | null>(null);
  const unitDisplay = useUnitPriceDisplay(tx);
  const categoryOptions = useCategoryOptions(projectId);

  // Amount and quantity stay linked by the transaction's own price — the amount
  // is never free-form. Editing one recomputes the other; when the FX rate loads
  // the amount is derived from the quantity (unless the user is driving amount).
  useEffect(() => {
    if (unitDisplay > 0 && touched !== 'amount') {
      setAmount(money2((parseFloat(quantity) || 0) * unitDisplay));
    }
  }, [quantity, unitDisplay, touched]);

  const onQuantity = (value: string) => {
    setTouched('qty');
    setQuantity(value);
  };

  const onAmount = (value: string) => {
    setTouched('amount');
    setAmount(value);
    setQuantity(unitDisplay > 0 ? qty8((parseFloat(value) || 0) / unitDisplay) : quantity);
  };

  const projectOptions = (projects ?? []).filter(project => project.isOwner).map(p => ({ value: p.id, label: p.name }));

  const onSubmit = async () => {
    if (!projectId || !bucketId || !amount) {
      error({ message: translate('cryptoGest.pool.pickProjectCategory') });

      return;
    }

    try {
      await create.mutateAsync({
        projectId,
        sourceEntryId: tx.id,
        bucketId,
        amountFiat: amount,
        quantity: quantity || null,
        unitPriceFiat: unitDisplay > 0 ? String(unitDisplay) : tx.unitPriceFiat,
        direction: tx.direction,
        date: tx.timestamp
      });

      success({ message: translate('cryptoGest.pool.assigned') });
      onClose();
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={open => !open && onClose()} open>
      <DialogContent onInteractOutside={event => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.pool.assignTitle')}</DialogTitle>
        </DialogHeader>

        <div className={'space-y-4'}>
          <div className={'text-sm text-muted-foreground'}>
            {tx.amount} {tx.symbol}
          </div>

          <div className={'space-y-1'}>
            <label className={'text-xs font-medium text-muted-foreground'}>
              {translate('cryptoGest.pool.project')}
            </label>
            <Combobox
              onChange={value => {
                setProjectId(value ?? '');
                setBucketId('');
              }}
              options={projectOptions}
              placeholder={translate('cryptoGest.pool.project')}
              value={projectId || null}
            />
          </div>

          <div className={'space-y-1'}>
            <label className={'text-xs font-medium text-muted-foreground'}>
              {translate('cryptoGest.pool.category')}
            </label>
            <Combobox
              onChange={value => setBucketId(value ?? '')}
              options={categoryOptions}
              placeholder={translate('cryptoGest.pool.category')}
              value={bucketId || null}
            />
          </div>

          <div className={'grid grid-cols-2 gap-3'}>
            <div className={'space-y-1'}>
              <label className={'text-xs font-medium text-muted-foreground'}>
                {translate('cryptoGest.pool.quantity')}
              </label>
              <Input onChange={event => onQuantity(event.target.value)} value={quantity} />
            </div>

            <div className={'space-y-1'}>
              <label className={'text-xs font-medium text-muted-foreground'}>
                {translate('cryptoGest.pool.amount')}
              </label>
              <Input onChange={event => onAmount(event.target.value)} type={'number'} value={amount} />
            </div>
          </div>
        </div>

        <div className={'mt-6 flex justify-end'}>
          <Button disabled={create.isLoading} onClick={onSubmit}>
            {translate('cryptoGest.pool.assign')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One leg of a split (its own project + category + quantity + amount).
 */

type Leg = { projectId: string; bucketId: string; quantity: string; amount: string };

function SplitLeg({
  leg,
  index,
  projectOptions,
  unitDisplay,
  remainingQty,
  symbol,
  onChange,
  onRemove
}: {
  leg: Leg;
  index: number;
  projectOptions: { value: string; label: string }[];
  unitDisplay: number;
  remainingQty: number;
  symbol: string;
  onChange: (index: number, leg: Leg) => void;
  onRemove: (index: number) => void;
}) {
  const [translate] = useTranslation();
  const categoryOptions = useCategoryOptions(leg.projectId);

  // Editing quantity or amount keeps the other in sync via the tx price.
  const setQuantity = (value: string) =>
    onChange(index, {
      ...leg,
      quantity: value,
      amount: unitDisplay > 0 ? money2((parseFloat(value) || 0) * unitDisplay) : leg.amount
    });
  const setAmount = (value: string) =>
    onChange(index, {
      ...leg,
      amount: value,
      quantity: unitDisplay > 0 ? qty8((parseFloat(value) || 0) / unitDisplay) : leg.quantity
    });
  // Fill this leg with whatever is still unallocated (quantity is the source of
  // truth, so the split adds up exactly; the amount follows from the price).
  const fillRemaining = () =>
    onChange(index, {
      ...leg,
      quantity: qty8(remainingQty),
      amount: unitDisplay > 0 ? money2(remainingQty * unitDisplay) : leg.amount
    });
  const canFill = remainingQty > 1e-9;
  const fillButton = (
    <button
      className={
        'absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10'
      }
      onClick={fillRemaining}
      title={`${qty8(remainingQty)} ${symbol}`}
      type={'button'}
    >
      {translate('cryptoGest.pool.fillRemaining')}
    </button>
  );

  return (
    <div className={'grid grid-cols-[1fr_1fr_auto] items-end gap-2 rounded-lg border border-border p-3'}>
      <div className={'space-y-1'}>
        <label className={'text-xs font-medium text-muted-foreground'}>{translate('cryptoGest.pool.project')}</label>
        <Combobox
          onChange={value => onChange(index, { ...leg, projectId: value ?? '', bucketId: '' })}
          options={projectOptions}
          value={leg.projectId || null}
        />
      </div>

      <div className={'space-y-1'}>
        <label className={'text-xs font-medium text-muted-foreground'}>{translate('cryptoGest.pool.category')}</label>
        <Combobox
          onChange={value => onChange(index, { ...leg, bucketId: value ?? '' })}
          options={categoryOptions}
          value={leg.bucketId || null}
        />
      </div>

      <Button className={'h-9 w-9'} onClick={() => onRemove(index)} size={'icon'} variant={'ghost'}>
        <X className={'h-4 w-4'} />
      </Button>

      <div className={'space-y-1'}>
        <label className={'text-xs font-medium text-muted-foreground'}>{translate('cryptoGest.pool.quantity')}</label>
        <div className={'relative'}>
          <Input
            className={cn(!leg.quantity && canFill && 'pr-28')}
            onChange={event => setQuantity(event.target.value)}
            value={leg.quantity}
          />
          {!leg.quantity && canFill ? fillButton : null}
        </div>
      </div>

      <div className={'space-y-1'}>
        <label className={'text-xs font-medium text-muted-foreground'}>{translate('cryptoGest.pool.amount')}</label>
        <div className={'relative'}>
          <Input
            className={cn(!leg.amount && canFill && 'pr-28')}
            onChange={event => setAmount(event.target.value)}
            type={'number'}
            value={leg.amount}
          />
          {!leg.amount && canFill ? fillButton : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Split the transaction across several pools.
 */

function SplitDialog({ tx, onClose }: { tx: PoolAllocatableTx; onClose: () => void }) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: projects } = usePoolInvestProjects();
  const create = useCreatePoolInvestFromCryptoGest();
  const [legs, setLegs] = useState<Leg[]>([
    { projectId: '', bucketId: '', quantity: '', amount: '' },
    { projectId: '', bucketId: '', quantity: '', amount: '' }
  ]);

  const { unitEur: unitDisplay, unitUsd } = useTxPricing(tx);
  const projectOptions = (projects ?? []).filter(project => project.isOwner).map(p => ({ value: p.id, label: p.name }));
  const txQty = parseFloat(tx.amount) || 0;
  const allocated = legs.reduce((sum, leg) => sum + (parseFloat(leg.quantity) || 0), 0);
  const over = allocated > txQty + 1e-9;
  const remainingQty = Math.max(txQty - allocated, 0);
  // The split must add up exactly to the transaction's quantity.
  const matches = txQty > 0 && Math.abs(allocated - txQty) < 1e-8;
  const ready = legs.filter(leg => leg.projectId && leg.bucketId && leg.quantity && leg.amount);
  const partial = legs.some(
    leg =>
      (leg.projectId || leg.bucketId || leg.quantity || leg.amount) &&
      !(leg.projectId && leg.bucketId && leg.quantity && leg.amount)
  );
  const canSplit = matches && ready.length > 0 && !partial && !create.isLoading;

  const update = (index: number, leg: Leg) => setLegs(current => current.map((item, i) => (i === index ? leg : item)));
  const remove = (index: number) => setLegs(current => current.filter((_, i) => i !== index));
  const add = () => setLegs(current => [...current, { projectId: '', bucketId: '', quantity: '', amount: '' }]);

  const onSubmit = async () => {
    if (!canSplit) {
      return;
    }

    try {
      for (const leg of ready) {
        await create.mutateAsync({
          projectId: leg.projectId,
          sourceEntryId: tx.id,
          bucketId: leg.bucketId,
          amountFiat: leg.amount,
          quantity: leg.quantity,
          unitPriceFiat: unitDisplay > 0 ? String(unitDisplay) : tx.unitPriceFiat,
          direction: tx.direction,
          date: tx.timestamp
        });
      }

      success({ message: translate('cryptoGest.pool.split') });
      onClose();
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={open => !open && onClose()} open>
      <DialogContent className={'max-w-2xl'} onInteractOutside={event => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.pool.splitTitle')}</DialogTitle>
        </DialogHeader>

        <div className={'space-y-3'}>
          <div
            className={cn(
              'flex items-start justify-between gap-4 rounded-md px-2 py-1.5 text-sm',
              matches ? 'bg-emerald-500/10' : over ? 'bg-destructive/10' : ''
            )}
          >
            <span className={'flex flex-col'}>
              <span className={'font-medium tabular-nums text-foreground'}>
                {tx.amount} {tx.symbol}
              </span>
              {unitDisplay > 0 ? (
                <span className={'text-xs tabular-nums text-muted-foreground'}>
                  {money(txQty * unitDisplay, 'EUR')}
                  {unitUsd > 0 ? ` · ${money(txQty * unitUsd, 'USD')}` : null}
                </span>
              ) : null}
            </span>

            <span className={'flex flex-col items-end'}>
              <span
                className={cn(
                  'font-medium tabular-nums',
                  matches ? 'text-emerald-600' : over ? 'text-destructive' : 'text-muted-foreground'
                )}
              >
                {translate('cryptoGest.pool.allocated')}
                {': '}
                {qty8(allocated)}
                {' / '}
                {txQty} {tx.symbol}
              </span>
              <span className={cn('text-xs tabular-nums', over ? 'text-destructive' : 'text-muted-foreground')}>
                {over
                  ? translate('cryptoGest.pool.overAllocated')
                  : `${translate('cryptoGest.pool.remaining')}: ${qty8(remainingQty)} ${tx.symbol}${
                      unitDisplay > 0 ? ` · ${money(remainingQty * unitDisplay, 'EUR')}` : ''
                    }`}
              </span>
            </span>
          </div>

          {legs.map((leg, index) => (
            <SplitLeg
              index={index}
              key={index}
              leg={leg}
              onChange={update}
              onRemove={remove}
              projectOptions={projectOptions}
              remainingQty={remainingQty}
              symbol={tx.symbol}
              unitDisplay={unitDisplay}
            />
          ))}

          <Button onClick={add} size={'sm'} variant={'outline'}>
            <Plus className={'h-4 w-4'} />
            {translate('cryptoGest.pool.addLeg')}
          </Button>
        </div>

        <div className={'mt-6 flex justify-end'}>
          <Button disabled={!canSplit} onClick={onSubmit}>
            {translate('cryptoGest.pool.split')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Export `PoolAllocationCell` component.
 *
 * Per-transaction pool actions — assign to a pool, split across pools, and a
 * badge showing where an allocated transaction went (with undo).
 */

export function PoolAllocationCell({ tx, links }: { tx: PoolAllocatableTx; links: PiCgLink[] }): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const remove = useRemovePoolInvestCryptoGestLinks();
  const { data: fx } = useCryptoGestFxAt(tx.timestamp);
  const [mode, setMode] = useState<null | 'assign' | 'split'>(null);
  const [open, setOpen] = useState(false);
  const linked = links.length > 0;
  // USD per EUR at the transaction's own date, to show the dollar value beside
  // the pool's EUR amount (consistent with the rest of the app).
  const usdPerEurAt = fx?.usdPerEur ?? 0;
  // Totals across every allocation leg — in the asset, in EUR and in USD.
  const totalQty = links.reduce((sum, link) => sum + (parseFloat(link.quantity ?? '0') || 0), 0);
  const totalFiat = links.reduce((sum, link) => sum + (parseFloat(link.amountFiat) || 0), 0);
  const totalCurrency = links[0]?.currency ?? 'EUR';
  const totalUsd = totalCurrency === 'EUR' && usdPerEurAt > 0 ? totalFiat * usdPerEurAt : null;

  const onRemove = async () => {
    try {
      await remove.mutateAsync(tx.id);
      success({ message: translate('cryptoGest.pool.removed') });
      setOpen(false);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  return (
    <div className={'flex items-center justify-end gap-1'} onClick={event => event.stopPropagation()}>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button className={'h-7 w-7'} size={'icon'} title={translate('cryptoGest.pool.menu')} variant={'ghost'}>
            <PiggyBank className={`h-3.5 w-3.5 ${linked ? 'text-primary' : 'text-muted-foreground'}`} />
          </Button>
        </PopoverTrigger>

        <PopoverContent align={'end'} className={'w-72 p-2'}>
          {linked && (
            <div className={'mb-1 border-b border-border pb-2'}>
              <div className={'px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
                {translate('cryptoGest.pool.allocatedTo')}
              </div>

              <div className={'space-y-0.5'}>
                {links.map(link => {
                  const currency = link.currency ?? 'EUR';
                  const fiat = parseFloat(link.amountFiat) || 0;
                  const usd = currency === 'EUR' && usdPerEurAt > 0 ? fiat * usdPerEurAt : null;

                  return (
                    <div
                      className={'flex items-center justify-between gap-3 rounded-md px-2 py-1'}
                      key={link.projectId}
                    >
                      <span className={'flex min-w-0 flex-col'}>
                        <span className={'flex min-w-0 items-center gap-1.5'}>
                          <PiggyBank className={'h-3.5 w-3.5 shrink-0 text-primary'} />
                          <span className={'truncate text-sm font-medium text-foreground'}>{link.projectName}</span>
                        </span>
                        <span className={'pl-5 text-[11px] tabular-nums text-muted-foreground'}>
                          {fmtQty(link.quantity)} {tx.symbol}
                        </span>
                      </span>

                      <span className={'flex shrink-0 flex-col items-end'}>
                        <span className={'text-sm font-medium tabular-nums text-foreground'}>
                          {money(fiat, currency)}
                        </span>
                        {usd != null ? (
                          <span className={'text-[11px] tabular-nums text-muted-foreground'}>{money(usd, 'USD')}</span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>

              {links.length > 1 ? (
                <div className={'mt-1 flex items-center justify-between gap-3 border-t border-border px-2 pt-1.5'}>
                  <span className={'flex min-w-0 flex-col'}>
                    <span className={'text-xs font-semibold uppercase tracking-wide text-muted-foreground'}>
                      {translate('cryptoGest.pool.total')}
                    </span>
                    <span className={'text-[11px] tabular-nums text-muted-foreground'}>
                      {qty8(totalQty)} {tx.symbol}
                    </span>
                  </span>

                  <span className={'flex shrink-0 flex-col items-end'}>
                    <span className={'text-sm font-semibold tabular-nums text-foreground'}>
                      {money(totalFiat, totalCurrency)}
                    </span>
                    {totalUsd != null ? (
                      <span className={'text-[11px] tabular-nums text-muted-foreground'}>{money(totalUsd, 'USD')}</span>
                    ) : null}
                  </span>
                </div>
              ) : null}

              <Button
                className={'mt-1 w-full justify-start text-destructive hover:text-destructive'}
                disabled={remove.isLoading}
                onClick={onRemove}
                size={'sm'}
                variant={'ghost'}
              >
                <Trash2 className={'h-3.5 w-3.5'} />
                {translate('cryptoGest.pool.remove')}
              </Button>
            </div>
          )}

          {!linked && (
            <>
              <Button
                className={'w-full justify-start'}
                onClick={() => {
                  setMode('assign');
                  setOpen(false);
                }}
                size={'sm'}
                variant={'ghost'}
              >
                <PiggyBank className={'h-3.5 w-3.5'} />
                {translate('cryptoGest.pool.assign')}
              </Button>

              <Button
                className={'w-full justify-start'}
                onClick={() => {
                  setMode('split');
                  setOpen(false);
                }}
                size={'sm'}
                variant={'ghost'}
              >
                <Split className={'h-3.5 w-3.5'} />
                {translate('cryptoGest.pool.split')}
              </Button>
            </>
          )}
        </PopoverContent>
      </Popover>

      {mode === 'assign' && <AssignDialog onClose={() => setMode(null)} tx={tx} />}
      {mode === 'split' && <SplitDialog onClose={() => setMode(null)} tx={tx} />}
    </div>
  );
}
